import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Search } from 'lucide-react'

import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { classifySearchInput } from '@/shared/lib/validators'

interface GlobalSearchProps {
  placeholder?: string
  variant?: 'header' | 'home'
  style?: React.CSSProperties
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  placeholder = 'Search by Address / Txn Hash / Block',
  variant = 'header',
  style,
}) => {
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const classified = classifySearchInput(query)
    if (!classified) {
      setError('Please enter a valid address, transaction hash, or block height.')
      return
    }
    setError(null)
    const { kind, value } = classified
    if (kind === 'tx') navigate(`/tx/${value}`)
    else if (kind === 'address') navigate(`/address/${value}`)
    else navigate(`/block/${value}`)
    setQuery('')
  }

  const isHeader = variant === 'header'

  return (
    <div style={{ width: '100%', ...style }}>
      <form
        className="search-wrapper"
        onSubmit={handleSearch}
        role="search"
        aria-label="Global search"
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          backgroundColor: 'var(--app-card-bg)',
          borderRadius: isHeader ? 'var(--radius-md)' : 'var(--radius-lg)',
          padding: isHeader
            ? isMobile
              ? 'var(--space-sm) var(--space-lg)'
              : 'var(--space-md) var(--space-xl)'
            : 'var(--space-14) var(--space-20)',
          border: `1px solid ${error ? 'var(--app-down)' : 'var(--app-divider)'}`,
          overflow: 'hidden',
          backgroundClip: 'padding-box',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
      >
        <Search
          size={isHeader && isMobile ? 14 : isHeader ? 16 : isMobile ? 20 : 24}
          style={{
            color: 'var(--app-text-secondary)',
            marginRight: isHeader
              ? isMobile
                ? 'var(--space-sm)'
                : 'var(--space-md)'
              : isMobile
                ? 'var(--space-lg)'
                : 'var(--space-xl)',
          }}
        />
        <input
          type="text"
          placeholder={isMobile && variant === 'header' ? 'Search...' : placeholder}
          value={query}
          aria-label="Search"
          aria-invalid={error != null}
          onChange={(e) => {
            setQuery(e.target.value)
            if (error) setError(null)
          }}
          style={{
            flex: 1,
            fontSize: isHeader
              ? isMobile
                ? 'var(--font-size-md)'
                : 'var(--font-size-lg)'
              : isMobile
                ? 'var(--font-size-xl)'
                : 'var(--font-size-h3)',
            background: 'transparent',
            color: 'var(--app-text-primary)',
            border: 'none',
            outline: 'none',
          }}
        />
        {!isHeader && (
          <button
            type="submit"
            style={{
              backgroundColor: 'var(--brand-primary)',
              color: 'var(--brand-on-primary)',
              padding: isMobile ? 'var(--space-md) var(--space-xl)' : 'var(--space-md) var(--space-2xl)',
              borderRadius: 'var(--radius-md)',
              fontWeight: 'bold',
              fontSize: isMobile ? 'var(--font-size-lg)' : undefined,
              marginLeft: 'var(--space-lg)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Search
          </button>
        )}
      </form>
      {error && (
        <div
          role="alert"
          style={{
            color: 'var(--app-down)',
            fontSize: 'var(--font-size-sm)',
            marginTop: 'var(--space-md)',
            paddingLeft: 'var(--space-xs)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  )
}

export default GlobalSearch
